import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { PrimaryButton } from '../common/PrimaryButton';
import { SecondaryButton } from '../common/SecondaryButton';

interface FeatureDefinition {
  displayName: string;
  category?: string;
  importance?: string;
}

interface FeatureDefinitionsProps {
  featureDefinitions: Record<string, FeatureDefinition> | undefined;
  onChange: (featureDefinitions: Record<string, FeatureDefinition>) => void;
}

const CATEGORIES = ['integrations', 'core_features', 'advanced_features', 'workflow', 'reporting', 'automation'];
const IMPORTANCE_LEVELS = ['low', 'medium', 'high']; // must match backend FeatureDefinition.importance

export const FeatureDefinitions: React.FC<FeatureDefinitionsProps> = ({
  featureDefinitions = {},
  onChange,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState<{ key: string; feature: FeatureDefinition } | null>(null);
  const [formData, setFormData] = useState<FeatureDefinition & { name: string }>({
    name: '',
    displayName: '',
    category: 'core_features',
    importance: 'medium',
  });

  const features = Object.entries(featureDefinitions).map(([key, value]) => ({
    key,
    ...value,
  }));

  const handleAdd = () => {
    setEditingFeature(null);
    setFormData({
      name: '',
      displayName: '',
      category: 'core_features',
      importance: 'medium',
    });
    setDialogOpen(true);
  };

  const handleEdit = (key: string, feature: FeatureDefinition) => {
    setEditingFeature({ key, feature });
    setFormData({
      name: key,
      displayName: feature.displayName,
      category: feature.category || 'core_features',
      importance: feature.importance || 'medium',
    });
    setDialogOpen(true);
  };

  const handleDelete = (key: string) => {
    const updated = { ...featureDefinitions };
    delete updated[key];
    onChange(updated);
  };

  const handleSave = () => {
    if (!formData.name) return;

    const updated = { ...featureDefinitions };
    const featureKey = editingFeature?.key || formData.name;

    // If renaming, delete old key
    if (editingFeature && editingFeature.key !== formData.name) {
      delete updated[editingFeature.key];
    }

    updated[featureKey] = {
      displayName: formData.displayName || formData.name,
      category: formData.category,
      importance: formData.importance,
    };

    onChange(updated);
    setDialogOpen(false);
  };

  const getCategoryColor = (category?: string) => {
    const colors: Record<string, string> = {
      integrations: '#b8e6d5',
      core_features: '#ffd9a8',
      advanced_features: '#c4c4f7',
      workflow: '#ffe8b3',
      reporting: '#d4f4dd',
      automation: '#ffdbe6',
    };
    return colors[category || ''] || '#e0e0e0';
  };

  const getImportanceColor = (importance?: string) => {
    const colors: Record<string, string> = {
      low: 'default',
      medium: 'primary',
      high: 'warning',
      critical: 'error',
    };
    return colors[importance || 'medium'] as any;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Feature Definitions
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Define features that will be tracked via SDK events (e.g., veeqoConnected, reportsGenerated)
          </Typography>
        </Box>
        <PrimaryButton startIcon={<AddIcon />} onClick={handleAdd}>
          Add Feature
        </PrimaryButton>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell width={180}>Feature Name</TableCell>
              <TableCell width={200}>Display Name</TableCell>
              <TableCell width={150}>Category</TableCell>
              <TableCell width={120}>Importance</TableCell>
              <TableCell width={100} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {features.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No features defined. Click "Add Feature" to create one.
                </TableCell>
              </TableRow>
            ) : (
              features.map((feature) => (
                <TableRow key={feature.key} hover>
                  <TableCell>
                    <Chip
                      label={feature.key}
                      size="small"
                      sx={{
                        bgcolor: '#b8e6d5',
                        color: '#1B0826',
                        fontWeight: 500,
                        fontFamily: 'monospace',
                      }}
                    />
                  </TableCell>
                  <TableCell>{feature.displayName}</TableCell>
                  <TableCell>
                    <Chip
                      label={feature.category}
                      size="small"
                      sx={{
                        bgcolor: getCategoryColor(feature.category),
                        color: '#1B0826',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={feature.importance}
                      size="small"
                      color={getImportanceColor(feature.importance)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(feature.key, feature)}
                      sx={{ mr: 0.5 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(feature.key)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingFeature ? `Edit Feature: ${editingFeature.key}` : 'Add New Feature'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Feature Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., veeqoConnected, reportsGenerated"
              helperText="Unique identifier (camelCase). Used in SDK events."
              disabled={editingFeature !== null}
            />

            <TextField
              fullWidth
              label="Display Name"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              placeholder="e.g., Veeqo Integration, Report Generation"
              helperText="Human-readable name shown in UI"
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                label="Category"
              >
                {CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat.replace(/_/g, ' ')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Importance</InputLabel>
              <Select
                value={formData.importance}
                onChange={(e) => setFormData({ ...formData, importance: e.target.value })}
                label="Importance"
              >
                {IMPORTANCE_LEVELS.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={() => setDialogOpen(false)}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSave} disabled={!formData.name}>
            Save
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
